import { useId, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { CircleCheck, Columns2, FolderClosed, Receipt, Trash2, X } from 'lucide-react';
import { getErrorMessage, keys, portalQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate, formatNumber } from '@blimo/shared';
import {
  Alert,
  AlertDescription,
  Button,
  CommentForm,
  CommentThreads,
  ConfirmDialog,
  DocumentList,
  DocumentViewer,
  ErrorState,
  Label,
  SearchInput,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { CheckBadges } from './list.jsx';
import { PageHeader } from '../../components/ui/page-header.jsx';
import { ItemIcon } from '../../components/ui/section.jsx';
import { Pager } from '../../components/ui/pager.jsx';

/** Belegprüfung – folders, document view with comments, compare view (auditors), booking list (auditors), confirmation. */
export function DocumentCheckPage() {
  const api = useApi();
  const { checkId } = useParams({ strict: false });
  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const { data: check, error, isPending, refetch } = useQuery(portalQueries.documentCheck(api, checkId));

  /** @param {Record<string, string|number|undefined>} patch */
  const setSearch = (patch) =>
    navigate({
      to: '/belegpruefung/$checkId',
      params: { checkId },
      search: (prev) => Object.fromEntries(Object.entries({ ...prev, ...patch }).filter(([, v]) => v !== undefined && v !== '')),
    });

  const documents = useMemo(() => (check?.folders ?? []).flatMap((folder) => folder.documents ?? []), [check]);

  const back = { to: '/belegpruefung', label: 'Belegprüfung' };

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Belegprüfung" back={back} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Belegprüfung nicht gefunden"
          notFoundDescription="Diese Prüfung gibt es nicht oder sie ist für Sie (noch) nicht freigegeben."
          action={
            <Button asChild variant="outline">
              <Link to="/belegpruefung">Zur Übersicht</Link>
            </Button>
          }
        />
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label="Wird geladen">
        <PageHeader title="Belegprüfung" back={back} />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const findDoc = (id) => documents.find((d) => String(d.id) === String(id));
  const compareId = check.i_am_auditor ? search.compare : undefined;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        back={back}
        title={`Belegprüfung ${check.fiscal_year}`}
        description={
          <span className="flex flex-col gap-1">
            <span>
              {check.property?.name}
              {check.due_on ? ` · Prüfung bis ${formatDate(check.due_on)}` : ''}
            </span>
            <CheckBadges check={check} />
          </span>
        }
        actions={check.i_am_auditor && !check.confirmed_by_me && <ConfirmCheckButton check={check} />}
      />
      {check.i_am_auditor && check.confirmed_by_me && (
        <Alert variant="success">
          <CircleCheck aria-hidden="true" />
          <AlertDescription>Sie haben diese Prüfung bestätigt. Vielen Dank!</AlertDescription>
        </Alert>
      )}

      {search.document && (
        <DocumentPanel
          check={check}
          documentId={search.document}
          compareId={compareId}
          documents={documents}
          findDoc={findDoc}
          onClose={() => setSearch({ document: undefined, compare: undefined })}
          onCompare={(id) => setSearch({ compare: id })}
        />
      )}

      <section aria-labelledby="folders-heading" className="flex flex-col gap-3">
        <h2 id="folders-heading" className="text-base font-semibold">
          Ordner
        </h2>
        {check.folders.length === 0 ? (
          <p className="rounded-xl border bg-card px-4 py-3 text-[0.8125rem] text-muted-foreground">
            Für Sie sind in dieser Prüfung noch keine Ordner freigegeben.
          </p>
        ) : (
          <div className="grid items-start gap-4 md:grid-cols-2">
            {check.folders.map((folder) => (
              <div
                key={folder.id}
                data-slot="card"
                className="flex flex-col overflow-hidden rounded-xl border bg-card [&>p]:px-4 [&>p]:py-3 [&>p]:text-[0.8125rem]"
              >
                <div className="flex items-center gap-3 border-b bg-muted/40 px-4 py-2.5">
                  <ItemIcon icon={FolderClosed} className="size-7" />
                  <h3 className="text-sm font-semibold">{folder.name}</h3>
                  <span className="ml-auto text-xs text-muted-foreground tabular-nums">{folder.documents?.length ?? 0}</span>
                </div>
                <DocumentList
                  label={`Dokumente ${folder.name}`}
                  documents={folder.documents}
                  activeId={search.document}
                  onOpen={(doc) => setSearch({ document: String(doc.id), compare: undefined })}
                  hrefFor={(doc) => api.portal.documentChecks.downloadUrl(check.id, doc.id)}
                  emptyText="Noch keine Dokumente in diesem Ordner."
                  className="rounded-none border-0 [&>li]:px-4"
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {check.i_am_auditor && check.has_booking_list && <BookingListSection checkId={check.id} page={search.page} onPage={(page) => setSearch({ page })} />}
    </div>
  );
}

function ConfirmCheckButton({ check }) {
  const api = useApi();
  const queryClient = useQueryClient();
  async function confirm() {
    try {
      const result = await api.portal.documentChecks.confirm(check.id);
      toast.success(result?.is_ready ? 'Prüfung bestätigt – alle Kassenprüfer haben bestätigt.' : 'Prüfung bestätigt');
      await queryClient.invalidateQueries({ queryKey: keys.portal.resource('document-checks') });
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Bestätigung ist fehlgeschlagen.'));
    }
  }
  return (
    <ConfirmDialog
      trigger={
        <Button>
          <CircleCheck aria-hidden="true" /> Prüfung abschließen
        </Button>
      }
      title="Prüfung abschließen?"
      description={`Sie bestätigen, dass Sie die Belege für ${check.fiscal_year} geprüft haben. Die Verwaltung wird informiert, sobald alle Kassenprüfer bestätigt haben.`}
      confirmLabel="Prüfung abschließen"
      onConfirm={confirm}
    />
  );
}

function DocumentPanel({ check, documentId, compareId, documents, findDoc, onClose, onCompare }) {
  const api = useApi();
  const selectId = useId();
  const doc = findDoc(documentId);
  const other = compareId ? findDoc(compareId) : null;
  const name = (d, id) => d?.title || d?.original_name || `Dokument #${id}`;
  const viewer = (id, d) => (
    <DocumentViewer
      title={name(d, id)}
      mimeType={d?.mime_type}
      src={api.portal.documentChecks.downloadUrl(check.id, id, { inline: true })}
      downloadHref={api.portal.documentChecks.downloadUrl(check.id, id)}
    />
  );

  return (
    <section aria-labelledby="document-heading" className="flex flex-col gap-4 rounded-xl border bg-card p-3 md:p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="document-heading" className="min-w-0 flex-1 break-words text-base font-semibold">
          {compareId ? 'Vergleich' : name(doc, documentId)}
        </h2>
        <Button variant="outline" size="sm" onClick={onClose} aria-label="Dokument schließen">
          <X aria-hidden="true" /> Schließen
        </Button>
      </div>
      {check.i_am_auditor && (
        <div className="flex flex-col gap-1">
          <Label htmlFor={selectId} className="text-xs! text-muted-foreground">
            <Columns2 className="mr-1 inline size-3.5" aria-hidden="true" />
            Vergleichen mit
          </Label>
          <div className="flex gap-2">
            <Select id={selectId} value={compareId ?? ''} onChange={(e) => onCompare(e.target.value || undefined)} wrapperClassName="flex-1 sm:max-w-md">
              <option value="">Kein Vergleich</option>
              {documents
                .filter((d) => String(d.id) !== String(documentId))
                .map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {name(d, d.id)}
                  </option>
                ))}
            </Select>
            {compareId && (
              <Button variant="ghost" size="icon" aria-label="Vergleich beenden" onClick={() => onCompare(undefined)}>
                <X aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
      )}
      {compareId ? (
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{name(doc, documentId)}</p>
            {viewer(documentId, doc)}
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{name(other, compareId)}</p>
            {viewer(compareId, other)}
          </div>
        </div>
      ) : (
        viewer(documentId, doc)
      )}
      <CommentsPanel checkId={check.id} documentId={documentId} compareId={compareId} />
    </section>
  );
}

function CommentsPanel({ checkId, documentId, compareId }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const params = compareId ? { document: String(documentId), compare: String(compareId) } : { document: String(documentId) };
  const comments = useQuery(portalQueries.documentCheckComments(api, checkId, params));
  const [toDelete, setToDelete] = useState(/** @type {any} */ (null));
  const invalidate = () => queryClient.invalidateQueries({ queryKey: [...keys.portal.detail('document-checks', checkId), 'comments'] });

  /** @param {string} body @param {any} [parent] */
  async function post(body, parent) {
    await api.portal.documentChecks.addComment(checkId, {
      document_id: Number(documentId),
      compare_document_id: compareId ? Number(compareId) : null,
      parent_id: parent ? parent.id : null,
      body,
    });
    toast.success(parent ? 'Antwort gespeichert' : 'Kommentar gespeichert');
    await invalidate();
  }

  async function remove() {
    try {
      await api.portal.documentChecks.deleteComment(checkId, toDelete.id);
      toast.success('Kommentar zurückgezogen');
      await invalidate();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Kommentar konnte nicht zurückgezogen werden.'));
    }
  }

  return (
    <section aria-labelledby="comments-heading" className="flex flex-col gap-3 border-t pt-4">
      <h3 id="comments-heading" className="text-sm font-semibold">
        {compareId ? 'Kommentare zum Vergleich' : 'Kommentare zum Dokument'}
      </h3>
      {comments.error ? (
        <ErrorState compact error={comments.error} onRetry={() => comments.refetch()} />
      ) : comments.isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <CommentThreads
          comments={comments.data}
          onReply={(parent, body) => post(body, parent)}
          actions={(comment) =>
            comment.is_mine && (
              <Button variant="ghost" size="icon-sm" aria-label="Kommentar zurückziehen" onClick={() => setToDelete(comment)}>
                <Trash2 aria-hidden="true" />
              </Button>
            )
          }
        />
      )}
      <CommentForm onSubmit={(body) => post(body)} />
      <ConfirmDialog
        open={Boolean(toDelete)}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Kommentar zurückziehen?"
        description="Der Kommentar wird für alle ausgeblendet."
        confirmLabel="Zurückziehen"
        destructive
        onConfirm={remove}
      />
    </section>
  );
}

/** Rows of the WinCasa booking list: cells may be a list or an object (raw CSV columns). */
const cellsOf = (row) => (Array.isArray(row.cells) ? row.cells : Object.values(row.cells ?? {}));

function BookingListSection({ checkId, page = 1, onPage }) {
  const api = useApi();
  const query = useQuery(portalQueries.documentCheckBookingList(api, checkId, { page }));
  const [q, setQ] = useState('');
  // 404 = no booking list / not an auditor → the section is hidden.
  if (query.error?.status === 404) return null;

  const body = query.data?.data;
  const meta = query.data?.meta;
  const header = Array.isArray(body?.header) ? body.header : body?.header ? Object.values(body.header) : [];
  const needle = q.trim().toLowerCase();
  const rows = (body?.rows ?? []).filter((row) => !needle || cellsOf(row).join(' ').toLowerCase().includes(needle));
  const lastPage = Math.max(1, meta?.last_page ?? 1);

  return (
    <section aria-labelledby="booking-heading" className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h2 id="booking-heading" className="text-base font-semibold">
          Buchungsliste
        </h2>
        {body && (
          <p className="text-[0.8125rem] text-muted-foreground">
            {[body.bank, body.period_from && body.period_to ? `${formatDate(body.period_from)} – ${formatDate(body.period_to)}` : null]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </div>
      {query.error ? (
        <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
      ) : query.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <SearchInput label="Buchungsliste durchsuchen" placeholder="Suchen (aktuelle Seite) …" value={q} onValueChange={setQ} delay={150} />
          <div className={query.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
            <Table aria-label="Buchungsliste" containerClassName="max-h-[70dvh]">
              {header.length > 0 && (
                <TableHeader>
                  <TableRow>
                    <TableHead data-numeric="true">Zeile</TableHead>
                    {header.map((h, i) => (
                      <TableHead key={i}>{h}</TableHead>
                    ))}
                    <TableHead>Beleg</TableHead>
                  </TableRow>
                </TableHeader>
              )}
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={header.length + 2} className="text-center text-muted-foreground">
                      Keine Buchungen gefunden.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.row}>
                      <TableCell data-numeric="true">{row.row}</TableCell>
                      {cellsOf(row).map((cell, i) => (
                        <TableCell key={i}>{cell}</TableCell>
                      ))}
                      <TableCell>
                        {row.receipt_document_id ? (
                          <Link
                            to="/belegpruefung/$checkId"
                            params={{ checkId: String(checkId) }}
                            search={(prev) => ({ ...prev, document: String(row.receipt_document_id), compare: undefined })}
                            className="inline-flex min-h-8 items-center gap-1 text-primary hover:underline"
                            aria-label={`Beleg zu Zeile ${row.row} öffnen`}
                          >
                            <Receipt className="size-4" aria-hidden="true" /> Beleg
                          </Link>
                        ) : (
                          <span className="text-muted-foreground">–</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <Pager
            always
            page={page}
            lastPage={lastPage}
            onPage={(next) => onPage(next > 1 ? next : undefined)}
            summary={<> · {formatNumber(meta?.total ?? 0)} Buchungen</>}
          />
        </>
      )}
    </section>
  );
}
